import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Award, MapPin, Truck, CalendarDays } from "lucide-react";
import { api } from "../../lib/api";
import { EmptyState } from "../../components/shared/EmptyState";

// Bloc de réassurance réutilisable (v1.5)
export const ReassurancePanel = () => (
  <div className="mt-12 grid gap-4 rounded-xl border border-sage/40 bg-sage-light p-6 sm:grid-cols-3" data-testid="reassurance-panel">
    <div className="flex items-start gap-3">
      <Award className="mt-0.5 h-5 w-5 shrink-0 text-deepgreen" />
      <p className="text-sm text-deepgreen"><strong>6 ans d'expérience</strong><br /><span className="text-muted-foreground">dans la décoration événementielle</span></p>
    </div>
    <div className="flex items-start gap-3">
      <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-deepgreen" />
      <p className="text-sm text-deepgreen"><strong>Retrait gratuit à Artres</strong><br /><span className="text-muted-foreground">au dépôt, sur rendez-vous</span></p>
    </div>
    <div className="flex items-start gap-3">
      <Truck className="mt-0.5 h-5 w-5 shrink-0 text-deepgreen" />
      <p className="text-sm text-deepgreen"><strong>Livraison jusqu'à 80 km</strong><br /><span className="text-muted-foreground">autour d'Artres (59)</span></p>
    </div>
  </div>
);

// Grille « Matériel associé » réutilisable (v1.5)
export const RelatedProducts = ({ products, title = "Matériel associé" }) => {
  if (!products?.length) return null;
  return (
    <div className="mt-12" data-testid="related-products">
      <h2 className="font-heading text-2xl text-deepgreen">{title}</h2>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => (
          <Link key={p.slug} to={`/produit/${p.slug}`} className="group card-ae overflow-hidden transition-transform duration-300 hover:-translate-y-1" data-testid={`related-product-${p.slug}`}>
            {p.image && <img src={p.image} alt={p.name} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
            <div className="p-4">
              {p.category && <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{p.category}</p>}
              <p className="mt-1 font-semibold text-deepgreen">{p.name}</p>
              {p.price_ttc != null && <p className="mt-1 text-sm text-muted-foreground">{p.price_ttc.toFixed(2)} € TTC / jour</p>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

// v1.6 : rendu fiable du contenu enrichi — neutralise les résidus Markdown (**gras**, ## titres)
// des anciens articles, sans jamais toucher au HTML Tiptap propre.
const inlineMd = (t) => t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>").replace(/\*([^*]+)\*/g, "<em>$1</em>");
const renderContent = (content) => {
  if (!content) return "";
  if (content.includes("<")) return content.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  return content.split(/\n{2,}/).map((block) => {
    const b = block.trim();
    if (b.startsWith("### ")) return `<h3>${inlineMd(b.slice(4))}</h3>`;
    if (b.startsWith("## ")) return `<h2>${inlineMd(b.slice(3))}</h2>`;
    if (b.startsWith("# ")) return `<h2>${inlineMd(b.slice(2))}</h2>`;
    if (b.startsWith("> ")) return `<blockquote>${inlineMd(b.slice(2))}</blockquote>`;
    if (/^[-*] /m.test(b)) return `<ul>${b.split("\n").map((l) => `<li>${inlineMd(l.replace(/^[-*] /, ""))}</li>`).join("")}</ul>`;
    return `<p>${inlineMd(b).replace(/\n/g, "<br />")}</p>`;
  }).join("");
};

export default function BlogArticlePage() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);
  const [similars, setSimilars] = useState([]);
  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));
  }, [slug]);
  useEffect(() => {
    if (!post) return;
    api.get("/blog").then((r) => {
      const pool = r.data.filter((p) => p.slug !== post.slug);
      const sameCat = pool.filter((p) => p.category === post.category);
      setSimilars([...sameCat, ...pool.filter((p) => p.category !== post.category)].slice(0, 2));
    }).catch(() => setSimilars([]));
  }, [post]);

  if (post === undefined) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="article-loading">Chargement…</div>;

  return (
    <div data-testid="blog-article-page">
      {post === null ? (
        <div className="container-ae py-12 md:py-16">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-deepgreen hover:text-sage" data-testid="article-back">
            <ArrowLeft className="h-4 w-4" /> Blog &amp; Conseils
          </Link>
          <div className="mt-8"><EmptyState title="Article introuvable">Cet article n'est plus publié — parcourez le blog.</EmptyState></div>
        </div>
      ) : (
        <>
          {/* Hero magazine : grande photo principale + titre élégant en surimpression */}
          <div className="relative overflow-hidden bg-deepgreen">
            {post.image && <img src={post.image} alt={post.title} className="absolute inset-0 h-full w-full object-cover opacity-50" data-testid="article-hero-image" />}
            <div className="absolute inset-0 bg-gradient-to-t from-deepgreen via-deepgreen/40 to-transparent" />
            <div className="container-ae relative pb-12 pt-14 md:pb-16 md:pt-20">
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-cream/80 hover:text-cream" data-testid="article-back">
                <ArrowLeft className="h-4 w-4" /> Blog &amp; Conseils
              </Link>
              <p className="overline-ae mt-8 !text-gold" data-testid="article-category">{post.category}</p>
              <h1 className="mt-3 max-w-3xl text-3xl text-cream sm:text-4xl lg:text-5xl" data-testid="article-title">{post.title}</h1>
              {post.published_at && (
                <p className="mt-4 flex items-center gap-2 text-sm text-cream/75" data-testid="article-date">
                  <CalendarDays className="h-4 w-4 text-gold" />
                  {new Date(post.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              )}
            </div>
          </div>

          <div className="container-ae max-w-3xl py-12 md:py-16">
            {/* Introduction attractive */}
            {post.excerpt && (
              <p className="border-l-4 border-gold pl-5 font-heading text-xl italic leading-relaxed text-deepgreen/85" data-testid="article-excerpt">
                {post.excerpt}
              </p>
            )}
            {/* Corps : paragraphes aérés, hiérarchie de titres, citations et images (prose-ae) */}
            <div className="prose-ae mt-10 text-base leading-relaxed" data-testid="article-body" dangerouslySetInnerHTML={{ __html: renderContent(post.content) }} />

            {/* Conclusion engageante + appel à l'action */}
            <div className="mt-14 rounded-2xl bg-deepgreen p-8 text-center md:p-10" data-testid="article-cta">
              <p className="font-accent text-2xl text-gold-light">Art Event</p>
              <p className="mt-3 font-heading text-xl text-cream sm:text-2xl">Envie d'une scénographie qui vous ressemble ?</p>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cream/75">
                Location de décoration, coordination de mariage : parlons de votre projet autour d'un café — à Artres ou chez vous.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to={post.cta_link || "/contact"} className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-bold uppercase tracking-[0.12em] text-deepgreen transition-colors hover:bg-cream" data-testid="article-cta-button">
                  {post.cta_label || "Parlez-nous de votre projet"} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/realisations" className="inline-flex items-center gap-2 rounded-full border border-cream/40 px-7 py-3 text-sm font-bold uppercase tracking-[0.12em] text-cream transition-colors hover:bg-white/10" data-testid="article-cta-realisations">
                  Nos réalisations
                </Link>
              </div>
            </div>

            {/* Articles similaires */}
            {similars.length > 0 && (
              <div className="mt-14" data-testid="similar-articles">
                <p className="overline-ae">À lire ensuite</p>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  {similars.map((s) => (
                    <Link key={s.slug} to={`/blog/${s.slug}`} className="group card-ae overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`similar-article-${s.slug}`}>
                      {s.image && <div className="overflow-hidden"><img src={s.image} alt={s.title} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>}
                      <div className="p-5">
                        {s.category && <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{s.category}</p>}
                        <p className="mt-1.5 font-heading text-lg leading-snug text-deepgreen">{s.title}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <RelatedProducts products={post.products} title="Matériel associé à cet article" />
            <ReassurancePanel />
          </div>
        </>
      )}
    </div>
  );
}
